import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import React, { useEffect, useState } from 'react';
import * as Highcharts from 'highcharts';
import HighchartsReact from 'highcharts-react-official';
import { useSelectionData } from '../../services/useSelectionData';
import { workforceDataService } from '../../services/WorkforceDataService';
import { getMetrics, getCategory, readStateValue, readCountyValue, formatMetricValue } from '../../services/metricRegistry';
import { getNarrativeScope, formatPlace } from '../../services/metricNarratives';
const SECTION_LABEL_CLASS = 'd-block text-uppercase fw-semibold mb-2 h6 mt-2';
// ============ Chart allowlist ============
//
// Which categories may render the comparison chart, per geography. This is an
// editorial gate, not a data one: a category is dropped here when the chart is
// not the right way to present it, regardless of whether the values exist.
//
// Keyed by NarrativeScope, not GeoLevel, because the policy distinguishes
// National from State — 'education' charts at State but not National — and
// those two share geoLevel 'state', differing only by which row is selected.
// getNarrativeScope owns that disambiguation and is reused here so this gate
// and the sidebar narrative can never disagree about which scope is in view.
//
// It can only ever subtract. A category still needs metrics from the registry
// at the requested geography (getMetrics -> metricAvailable, which checks
// stateField / countyField / hidden), so listing a scope here cannot conjure a
// chart the data does not support. To make a category chart where it currently
// does not, fix its descriptor in metricRegistry.ts — this table will not do it.
//
// Typed as a total Record rather than a Partial on purpose — adding a member to
// the CategoryId union is a compile error here until it is given a policy, so a
// new category cannot silently inherit a default.
const CHART_ALLOWLIST = {
    // Workforce — chart withheld entirely for these two.
    'home-visitors': [],
    'capacity': [],
    // Female holds exactly one metric, so this chart drew a single bar for it.
    // FemaleSharePie renders the same number as a share of the whole instead,
    // and mounts in the same place in the sidebar column. Restoring a scope
    // here would put both in the column at once.
    'female': [],
    'tenure': ['national', 'state'],
    'age': ['national', 'state'],
    'education': ['national', 'state'],
    // Adjacent Fields — charted at every scope.
    'earnings': ['national', 'state', 'county'],
    'graduates': ['national', 'state', 'county'],
    // Households — one category, no group dropdown; charted at every scope.
    'households': ['national', 'state', 'county']
};
/** True when the category is cleared to chart at this scope. */
function chartAllowed(categoryId, scope) {
    var _a;
    return categoryId ? ((_a = CHART_ALLOWLIST[categoryId]) === null || _a === void 0 ? void 0 : _a.includes(scope)) === true : false;
}
// Series colors. The prototype's V9_STATE_COLOR / V9_NATL_COLOR were not
// pasted with the rest of the code, so these are drawn from the site palette
// in dashboard.css (--mapdashboard-scope-accent) and the registry's pale blue. Swap the two
// hex values here if the prototype used different ones.
// #016fad / #9ca3af — the exact pair the state report's bar charts use
// (statereport.js). This was #145A9E, the dashboard's own accent, which read as
// a different blue beside the state profile's charts.
const STATE_COLOR = '#016fad';
// #9ca3af, the same grey the state report's bar charts use for their U.S.
// series (statereport.js), NOT the paler #c9d2db this had before.
//
// The lightness is what makes legend hover legible, so it is not a free choice.
// Highcharts fades the non-hovered series to opacity 0.2 and leaves the hovered
// one alone, so the whole effect depends on the two ending up at visibly
// different weights. At #c9d2db they did not: the pale grey at full opacity sat
// at about the same weight as STATE_COLOR dimmed to 0.2, so hovering either
// item changed the picture hardly at all and the highlight read as broken.
const NATL_COLOR = '#9ca3af';
// Hover is left entirely to Highcharts, as in the state report's bar charts
// (statereport.js), whose series carry nothing but name, data and color.
//
// What used to be here — a paintSeries() helper plus mouseOver/mouseOut events
// that repainted every bar in the U.S. series together, and a states.hover
// override with brightness zeroed — made hovering one bar light up the whole
// series. Highcharts' own hover is per point: the bar under the cursor lifts
// and nothing else moves. Hovering a legend item is the series-wide gesture,
// and it already fades the other series on its own (states.inactive).
//
// Text colors match --mapdashboard-scope-heading / --mapdashboard-scope-muted / --mapdashboard-scope-body in dashboard.css.
const LABEL_STYLE = {
    fontSize: '12px', fontWeight: '700', color: '#071437', textOutline: 'none'
};
const NATL_LABEL_STYLE = {
    fontSize: '12px', fontWeight: '600', color: '#071437', textOutline: 'none'
};
const AXIS_LABEL_STYLE = { color: '#071437', fontSize: '12px', width: 210 };
// ============ Rule above the legend ============
//
// Highcharts has no option for a line on one side of the legend —
// legend.borderWidth draws a box on all four — so it is stroked onto the chart
// by drawLegendRule below and repositioned on every render.
//
// The gap is applied twice: legend.margin reserves GAP*2 between the plot and
// the legend, and the rule is drawn one GAP above the legend, landing it
// midway. Same arrangement the old CSS key had as margin-top + padding-top
// either side of its border-top.
const LEGEND_RULE_COLOR = '#C9D2DB';
const LEGEND_RULE_GAP = 10;
/**
 * Draw the 1px rule above the legend.
 *
 * Cached on the chart rather than in a module variable: more than one chart can
 * be mounted at once (this one, FemaleSharePie's, the map), and a shared
 * variable would leave the last one to render holding the only reference.
 * Hanging it off the chart also means Highcharts destroys it with the chart.
 *
 * Re-running is cheap and idempotent — the path is moved, not re-added — which
 * matters because `render` fires on every redraw, and the legend's y moves with
 * the chart height and the number of bars.
 *
 * The half-pixel offset puts a 1px stroke on the pixel grid instead of
 * straddling two rows and rendering as a 2px blur.
 */
function drawLegendRule(chart) {
    var _a;
    const group = (_a = chart === null || chart === void 0 ? void 0 : chart.legend) === null || _a === void 0 ? void 0 : _a.group;
    if (!group)
        return;
    const y = Math.round(group.translateY - LEGEND_RULE_GAP) + 0.5;
    const d = [['M', 0, y], ['L', chart.chartWidth, y]];
    if (chart.hvLegendRule) {
        chart.hvLegendRule.attr({ d });
    }
    else {
        chart.hvLegendRule = chart.renderer
            .path(d)
            .attr({ stroke: LEGEND_RULE_COLOR, 'stroke-width': 1, zIndex: 1 })
            .add();
    }
}
// ============ Minimum bar length ============
//
// No bar is allowed to vanish. One floor, as a fraction of the longest bar on
// the chart, applied to every bar alike: null (suppressed / missing), a true
// zero, and a real value too small to survive the axis scale all draw at this
// length. Which of those three a bar is, is carried by its label — 'N/A', '<5',
// '0', or the number — never by its length.
//
// One value rather than a separate, longer mark for the no-data case. A longer
// no-data mark put 'N/A' above real quantities in every mixed chart: Alabama's
// graduates read N/A at 14px over Nursing's 4,232 at 6px. Bar length is the
// chart's primary encoding, so having it rank absence above presence was the
// one reading worth ruling out, and sharing the floor rules it out by
// construction.
//
// Relative, not absolute, because the axis rescales per category — a percent
// band tops out near 100, earnings run to six figures — so any fixed number
// would be an invisible sliver on one chart and a plausible-looking value on
// the next.
//
// Length only. The data label always prints the true text off Bar.formatted,
// and shares are always computed from the true value, so nothing downstream
// reads the floor as a quantity.
//
// Measured, not estimated — the arithmetic is not obvious. The fraction is
// taken against the DATA max, but what the eye sees is the fraction of the AXIS
// max, and Highcharts rounds the axis up to a round tick value well past what
// maxPadding asked for: a 45.2 data max with maxPadding 0.32 wants 59.7 and
// gets an axis of 75. That inflation ran 1.47x-1.73x across percent, count and
// dollar categories in the sidebar's ~163-185px plot, so the on-screen length
// is roughly this fraction divided by 1.5.
//
//     0.04 -> 4-5px    (reads as a rendering artifact)
//     0.06 -> 6px      <- here
//     0.08 -> 8-10px
//     0.15 -> 14-19px
//
// 0.06 rather than higher because the floor flattens every value below it to one
// length. Measured on graduates (national row, axis to 400,000), 0.06 lifts only
// Early childhood's 354 and leaves Social work's 22,393 at 9px distinct from
// Public health's 15,232 at 6px. At 0.15 all three collapse to 14px and the
// series stops carrying any ordering at all.
const MIN_BAR_FRACTION = 0.06;
/** Only numbers plot. Suppression sentinels ('<5' / 'Missing') become gaps. */
function numOrNull(raw) {
    return typeof raw === 'number' && Number.isFinite(raw) ? raw : null;
}
/**
 * The selected geography's value as a percentage of the geography it is being
 * compared against — part over whole, where the part is one state's or county's
 * count and the whole is the same metric one level up: the national count for a
 * state, the state count for a county. The share always describes the second
 * series, so it can never quote a denominator that is not on the chart.
 *
 * Returns null wherever the share would assert something the data does not
 * support, so the caller can fall back to the bare value: a suppressed or
 * missing part (numOrNull already made it null, and its label reads '<5' or
 * 'N/A'), or a comparison total of zero. That last case is the one worth
 * naming — zero statewide does not make a county 100% of anything, and the
 * division would hand back Infinity or NaN.
 *
 * Below a tenth of a point the share prints as '<0.1%' rather than rounding
 * to '0.0%'. Small parts against large wholes live down there — 41 nursing
 * graduates against a six-figure total is real data, and '0.0%' reads as none
 * at all.
 */
function formatShare(part, whole) {
    if (part === null || whole === null || whole <= 0)
        return null;
    const pct = (part / whole) * 100;
    if (!Number.isFinite(pct))
        return null;
    return pct > 0 && pct < 0.1 ? '<0.1%' : `${pct.toFixed(1)}%`;
}
/**
 * Read one metric off a row at the requested geography. County rows are used
 * only when a county is drilled into; everything else reads the state row,
 * which is how resolveMetricValue and the KPI header behave.
 */
function readAt(metric, countyRow, stateRow, useCounty) {
    var _a, _b;
    const raw = useCounty && countyRow
        ? readCountyValue(countyRow, metric.id)
        : stateRow ? readStateValue(stateRow, metric.id) : null;
    return {
        label: (_a = metric.shortLabel) !== null && _a !== void 0 ? _a : metric.label,
        value: numOrNull(raw),
        formatted: formatMetricValue(metric.id, raw),
        shortChartLabel: metric.shortChartLabel == null ? (_b = metric.shortLabel) !== null && _b !== void 0 ? _b : metric.label : metric.shortChartLabel
    };
}
/**
 * Sidebar comparison chart: a horizontal bar per metric in the selected
 * category. When a comparison row is available the bars pair up — a county
 * against its own state, any other selection against the United States —
 * otherwise a single series renders.
 *
 * Ported from the prototype's vanilla renderSideChart(). Two things changed in
 * the move: the chart mounts through <HighchartsReact> rather than an
 * imperative Highcharts.chart(el) call against a DOM id, so React owns the
 * lifecycle and the manual destroy() is gone; and the legend is JSX instead of
 * an innerHTML string, because the prototype's .hv-legend CSS is not in
 * dashboard.css.
 */
const CompareChart = ({ dashboardState }) => {
    var _a, _b;
    const { countyData, stateData, isLoading } = useSelectionData(dashboardState);
    const [nationalData, setNationalData] = useState(null);
    // The national row is a normal state-level row named "United States".
    // workforceDataService caches the state payload, so this costs no extra
    // request beyond the one useSelectionData already made.
    useEffect(() => {
        let cancelled = false;
        workforceDataService.getStateByName('United States')
            .then(row => { if (!cancelled)
            setNationalData(row); })
            .catch(() => { if (!cancelled)
            setNationalData(null); });
        return () => { cancelled = true; };
    }, []);
    const categoryId = dashboardState.selectedCategoryId;
    const category = getCategory(categoryId);
    // The tooltip's second line, standing in for the fixed subtitle the state
    // report's charts hard-code ("Number of Graduates"). Pulled out here so the
    // formatter closes over a plain string and the memo can depend on it.
    const categoryLabel = (_a = category === null || category === void 0 ? void 0 : category.label) !== null && _a !== void 0 ? _a : '';
    const useCounty = dashboardState.geoLevel === 'county' && dashboardState.selectedCounty != null;
    // spec.items in the prototype — here the category's own metrics, so the
    // chart cannot drift from the selector above it. getMetrics already drops
    // hidden descriptors; excludeFromChart drops the ones that belong in the
    // dropdown but not on a shared axis — Age's average in years would
    // otherwise plot as a seventh bar beside its six percent bands.
    const metrics = categoryId
        ? getMetrics(categoryId, dashboardState.geoLevel).filter(m => !m.excludeFromChart)
        : [];
    const geoBars = metrics.map(m => readAt(m, countyData, stateData, useCounty));
    // What the selection is measured against — its own state once a county is
    // drilled into, the national row for everything else. A county's counts say
    // very little beside a national total (a four-figure household count against
    // a nine-figure one flattens to nothing on a shared axis, and its share
    // rounds away), while the state is the level its numbers are actually
    // administered and compared at.
    //
    // stateData is already the right row and costs no extra request:
    // useSelectionData keys the county fetch on (selectedState, selectedCounty),
    // so whenever a county is selected, selectedState is that county's own state
    // — set by handleCountySelect from the clicked feature, not by the Geography
    // dropdown — and stateData is its row. Which state the dropdown last held is
    // therefore irrelevant here; the county determines its own comparison.
    const comparisonRow = useCounty ? stateData : nationalData;
    const compBars = metrics.map(m => readAt(m, null, comparisonRow, false));
    // True when the selection IS the national row, in which case readAt is
    // already reading that row and a second National series would duplicate it.
    // Mirrors formatPlace's own test rather than checking selectedState alone:
    // handleGeoLevelChange resets selectedState to 'United States' on entering
    // County view, so a bare selectedState check reads county level as national
    // even after a county has been clicked, and drops the comparison.
    const isNationalSelection = dashboardState.selectedCounty == null &&
        (!dashboardState.selectedState || dashboardState.selectedState === 'United States');
    // Reads comparisonRow, not nationalData: in county view the national row can
    // be present and still be the wrong whole, and the state row can be missing
    // (a county whose state name did not resolve), in which case the county's
    // bars stand alone rather than pairing with a series that is not there.
    const compare = comparisonRow != null && !isNationalSelection;
    // Identity of the first (and, without a comparison, only) series.
    //
    // When the selection resolves to the national row there is no second series
    // to compare against, so the lone series simply IS National and carries the
    // national name and color — never "the U.S." in one place and "National" in
    // another for the same numbers. Otherwise it names the selected geography:
    // "County, State" once a county is drilled into, else the state. formatPlace
    // is shared with the sidebar narrative so the two can never disagree about
    // whose numbers these are — that is why the series is named "Randolph,
    // Alabama" rather than the county alone. (The legend chip below drops the
    // state, which its neighboring chip already names.)
    const seriesLabel = isNationalSelection ? 'National' : formatPlace(dashboardState);
    const seriesColor = isNationalSelection ? NATL_COLOR : STATE_COLOR;
    // Identity of the second series, naming the row comparisonRow just picked:
    // the county's state by name, else the national row. Named rather than
    // generic ("State") so a screen reader hears which state the bars belong
    // to — the county series says "Randolph, Alabama" and this one "Alabama".
    const comparisonLabel = useCounty ? dashboardState.selectedState : 'National';
    const hasData = geoBars.some(b => b.value !== null)
        || (compare && compBars.some(b => b.value !== null));
    const options = React.useMemo(() => {
        if (!metrics.length || !hasData)
            return null;
        const categories = geoBars.map(b => {
            let finalLabel = b.shortChartLabel;
            return finalLabel;
        });
        // Share of the comparison total, per metric, for the selected geography
        // — of the state when a county is selected, of the nation otherwise,
        // following whichever row compBars was built from. Parallel to `metrics`
        // because geoBars and compBars are both built by mapping it, so index i
        // is the same metric in all three.
        //
        // Gated on `compare`, which is doing double duty and is exactly right
        // for both jobs: it is false when the comparison row never arrived (no
        // whole to divide by) and false when the selection IS the national row
        // (the part and the whole are the same number, and every bar would read
        // 100.0% of itself). Either way there is no share worth printing.
        //
        // The descriptor flag is still named shareOfNational; it marks which
        // metrics are meaningful as a part of a larger whole (graduate counts),
        // not which whole, and county charts now read that whole as the state.
        const shares = geoBars.map((b, i) => {
            var _a, _b, _c;
            return compare && ((_a = metrics[i]) === null || _a === void 0 ? void 0 : _a.shareOfNational)
                ? formatShare(b.value, (_c = (_b = compBars[i]) === null || _b === void 0 ? void 0 : _b.value) !== null && _c !== void 0 ? _c : null)
                : null;
        });
        // The longest bar actually being drawn, which both floors are measured
        // against. Reads both series whenever both are plotted, so a floored bar
        // in the selection's series and one in the comparison's come out the
        // same length — otherwise the pair reads as a real difference between
        // two things that are equally absent.
        const plottedMax = Math.max(0, ...geoBars.map(b => { var _a; return (_a = b.value) !== null && _a !== void 0 ? _a : 0; }), ...(compare ? compBars.map(b => { var _a; return (_a = b.value) !== null && _a !== void 0 ? _a : 0; }) : []));
        // Null when nothing positive is on the chart at all. There is no scale to
        // take a fraction of in that case: plottedMax * fraction is zero, and
        // substituting some constant instead would be worse — Highcharts would
        // scale the axis to the floors themselves and an all-zero category would
        // render as a full set of bars. Left unfloored.
        //const minBar = plottedMax > 0 ? plottedMax * MIN_BAR_FRACTION : null;
        /**
         * Length to plot: the value, held up to the floor. `?? 0` folds null in
         * with a true zero, so missing data and a real zero and a value too
         * small to render all come out at exactly the same length.
         */
        const plotY = (value) => Math.max(value !== null && value !== void 0 ? value : 0);
        // withShares is per-series, not per-chart: the comparison series passes
        // false so its bars keep the bare count. Appending the share there
        // would restate the denominator as a percentage of itself.
        //
        // y and custom.formatted come apart here for the first time: y may be
        // the stub, formatted is always built from the real value. That split is
        // the whole point — the bar is visible, the number stays honest.
        const toPoints = (bars, withShares) => bars.map((b, i) => {
            const share = withShares ? shares[i] : null;
            return {
                y: plotY(b.value),
                custom: { formatted: share ? `${b.formatted} (${share})` : b.formatted }
            };
        });
        // Drives the axis padding below — only charts that actually rendered a
        // share need the extra room for it.
        const anyShare = shares.some(s => s !== null);
        // name, data, color — and the dataLabels style this chart needs. Nothing
        // else, matching the state report's bar series, so hover on the bars and
        // on the legend are both Highcharts' own.
        //
        // Series names carry the geography, not the category — screen readers
        // announce them, they are what the legend prints, and naming the single
        // series after the metric group left the comparison bars unattributed.
        const series = compare
            ? [
                {
                    type: 'bar', name: seriesLabel, data: toPoints(geoBars, true),
                    color: seriesColor, dataLabels: { style: LABEL_STYLE }
                },
                {
                    type: 'bar', name: comparisonLabel, data: toPoints(compBars, false),
                    color: NATL_COLOR, dataLabels: { style: NATL_LABEL_STYLE }
                }
            ]
            : [
                {
                    // showInLegend no longer forced off: with the legend on, the
                    // key is the only thing naming these bars, and the
                    // single-series case is where that matters most — County
                    // view before a county is clicked draws the national row.
                    type: 'bar', name: seriesLabel,
                    data: toPoints(geoBars, true), color: seriesColor,
                    dataLabels: { style: LABEL_STYLE }
                }
            ];
        const pointWidth = compare ? 11 : 12;
        // + 34 for the legend row. The height here is explicit, so without the
        // extra Highcharts takes the legend's space out of the plot area and
        // every bar gets shorter by the height of the key.
        const height = (compare
            ? 36 * categories.length + 14
            : 28 * categories.length + 12) + 34;
        return {
            chart: {
                // Right spacing is the only non-zero side. Data labels are drawn
                // with crop:false / overflow:'allow', so they escape the plot area
                // but still clip at the chart box — this reserves the gutter they
                // spill into. Keep it in step with yAxis.maxPadding below.
                type: 'bar', backgroundColor: 'transparent', height,
                spacing: [0, 14, 0, 0], style: { fontFamily: 'inherit' },
                // Switching metric groups replaces the bars outright: different
                // count, different category labels, and a different `height`
                // above. HighchartsReact applies that through chart.update(),
                // which by default tweens old into new — bars slide between
                // metrics that have nothing to do with each other while the box
                // resizes under them. Nothing in that transition is readable,
                // because the before and after describe different measures, so
                // the new chart is drawn straight instead.
                //
                // This flag covers the update and the setSize it triggers. It
                // does NOT cover the first draw of a series, which is why
                // plotOptions.bar.animation is set as well — entering a
                // comparison adds a second series, and that one would otherwise
                // still grow in from zero beside its already-settled partner.
                animation: false,
                events: {
                    // Fires after every draw and redraw, which is what the rule
                    // needs: the legend's y moves with the chart height and the
                    // number of bars, and drawLegendRule repositions rather than
                    // re-adds, so running it every time is safe.
                    render: function () { drawLegendRule(this); }
                }
            },
            title: { text: undefined },
            xAxis: {
                categories, lineWidth: 0, tickWidth: 0,
                labels: { style: AXIS_LABEL_STYLE }
            },
            // maxPadding shortens the longest bar rather than the chart, leaving
            // room for its label. 0.22 was tight enough that wide values (six-digit
            // earnings) ran off the right edge; the axis stays hidden either way.
            //
            // Padding buys room by making every bar shorter, so it is tempting to
            // trade it down against the spacing gutter above. Do not: 0.26 with a
            // 14px gutter still clipped the widest label. 0.32 is the floor that
            // clears it, and the gutter cannot make up the difference.
            //
            // A share appends roughly " (12.3%)" to the label it sits on, near
            // half again the width of the six-digit value 0.32 was measured
            // against, so those charts get more room. The bump is proportional
            // to the added characters rather than measured — worth an eye on
            // the widest case (a five-figure state count beside its share)
            // before it is treated as a floor the way 0.32 is.
            yAxis: { visible: false, maxPadding: anyShare ? 0.42 : 0.32, min: 0 },
            // Same three lines the state report's bar charts use (statereport.js
            // — earnings, graduates, job density). Everything else is left at
            // Highcharts' defaults on purpose: the legend draws one item per
            // series off the series itself, and hovering an item already fades
            // the others (states.inactive) and lifts the hovered one. None of
            // that needs configuring, and configuring it is how the hand-built
            // key drifted out of step with the bars in the first place.
            legend: {
                layout: 'horizontal',
                align: 'left',
                verticalAlign: 'bottom',
                // Square markers. Highcharts renders a bar series' legend symbol
                // as a circle by defaulting symbolRadius to half the symbol
                // height, so squaring it means pinning that radius to 0 rather
                // than switching anything on — squareSymbol is already true and
                // on its own only forces the width to equal the height.
                //
                // 10x10 is the size the old hand-built key used. Stated rather
                // than left to default, because symbolHeight otherwise tracks
                // the item font size.
                symbolRadius: 0,
                symbolWidth: 10,
                symbolHeight: 10,
                // Room for the rule drawn above the legend by drawLegendRule.
                margin: LEGEND_RULE_GAP * 2
            },
            plotOptions: {
                bar: {
                    borderWidth: 0, borderRadius: 3, pointWidth,
                    pointPadding: 0.04, groupPadding: 0.08,
                    animation: false,
                    point: {
                        events: {
                            mouseOver: function () {
                                var _a, _b;
                                const hovered = this.series;
                                for (const s of (_b = (_a = hovered === null || hovered === void 0 ? void 0 : hovered.chart) === null || _a === void 0 ? void 0 : _a.series) !== null && _b !== void 0 ? _b : []) {
                                    if (s !== hovered)
                                        s.setState();
                                }
                            }
                        }
                    },
                    dataLabels: {
                        enabled: true, crop: false, overflow: 'allow', padding: 4,
                        formatter: function () {
                            var _a, _b, _c, _d;
                            // Pre-formatted per point, so each bar uses its own
                            // metric's unit (dollars / percent / count) instead
                            // of one formatter guessing for the whole series.
                            return (_d = (_c = (_b = (_a = this.point) === null || _a === void 0 ? void 0 : _a.options) === null || _b === void 0 ? void 0 : _b.custom) === null || _c === void 0 ? void 0 : _c.formatted) !== null && _d !== void 0 ? _d : 'N/A';
                        }
                    }
                }
            },
            // Ported from the state report's bar charts (statereport.js): the
            // same transparent-chrome options, the same .hc-tooltip / .tt-card
            // markup, and shared: true.
            //
            // `shared` is doing double duty and is the reason bar hover looks
            // right. Highcharts' Pointer fades every series not part of the
            // hover to states.inactive, which is why hovering one bar used to
            // dim the other series to 0.2. A shared hover treats every series
            // at that category as active, so none is marked inactive — that is
            // how the state report's charts avoid the same shading, not a
            // setting of their own.
            //
            // It only works while the tooltip is enabled. runPointActions reads
            //
            //     const tooltip = (chart.tooltip?.options.enabled ? chart.tooltip : void 0),
            //           shared  = (tooltip ? tooltip.shared : false);
            //
            // so turning the tooltip off silently discards `shared` and the
            // fade comes back. The two travel together.
            //
            // Legend hover is a separate path — Legend's own
            // setOtherItemsState('inactive') — so it still fades, as it should.
            //
            // Values come from point.custom.formatted, the string the data
            // label already prints, so the tooltip cannot disagree with the bar
            // it describes and each metric keeps its own unit (dollars,
            // percent, count) instead of one formatter guessing for all of
            // them. Markup uses the dashboard's .tt-list grid (.l / .v), which
            // is already styled here for the map tooltip — the state report's
            // flat <b> rows would land in that grid as single-column items.
            tooltip: {
                enabled: false,
                backgroundColor: 'transparent',
                borderWidth: 0,
                borderColor: 'transparent',
                shadow: false,
                useHTML: true,
                padding: 0,
                // The second class is a size modifier, not a second skin. The
                // .tt-* rules this borrows are shared with the map's tooltip,
                // which is a full-width card with room to breathe; this one
                // sits in the sidebar, so it scales the type and spacing down
                // without touching the map. See dashboard.css.
                className: 'hc-tooltip hc-tooltip-compact',
                shared: true,
                formatter: function () {
                    var _a, _b, _c, _d, _e, _f;
                    const pts = (_a = this.points) !== null && _a !== void 0 ? _a : [];
                    if (!pts.length)
                        return false;
                    // point.category, not this.x. The state report's formatters
                    // interpolate this.x and get the axis label, but they run on
                    // Highcharts 11 (the global highmaps.js); on 12, which this
                    // bundle uses, the formatter context hands back the x VALUE
                    // — the category index — so this.x printed "0" as the title.
                    const label = (_f = (_d = (_c = (_b = pts[0]) === null || _b === void 0 ? void 0 : _b.point) === null || _c === void 0 ? void 0 : _c.category) !== null && _d !== void 0 ? _d : (_e = pts[0]) === null || _e === void 0 ? void 0 : _e.key) !== null && _f !== void 0 ? _f : this.x;
                    const rows = pts.map(p => {
                        var _a, _b, _c, _d;
                        return `<div class="l">${p.series.name}</div>` +
                            `<div class="v">${(_d = (_c = (_b = (_a = p.point) === null || _a === void 0 ? void 0 : _a.options) === null || _b === void 0 ? void 0 : _b.custom) === null || _c === void 0 ? void 0 : _c.formatted) !== null && _d !== void 0 ? _d : 'N/A'}</div>`;
                    }).join('');
                    return `<div class="tt-card">`
                        + `<div class="tt-title">${label}</div>`
                        + (categoryLabel ? `<div class="tt-sub">${categoryLabel}</div>` : '')
                        + `<div class="tt-list">${rows}</div>`
                        + `</div>`;
                }
            },
            credits: { enabled: false },
            exporting: { enabled: false },
            series
        };
    }, [metrics, geoBars, compBars, compare, hasData, seriesLabel, seriesColor, comparisonLabel, isNationalSelection, categoryLabel]);
    // Names the comparison the chart is actually drawing, which is why it reads
    // `compare` / `useCounty` rather than getNarrativeScope's dropdown Level.
    // At County level before a county is clicked — and, since 2026-08-26, at
    // State level before a state is picked — the selection resolves to the
    // national row, so a single National series renders and there is no "vs."
    // to announce; the dropdown would still have said 'county'/'state' and
    // headed a chart containing no county or state. The same fallback covers a
    // failed national fetch (nothing to compare against) and the no-data case,
    // so the heading never promises a comparison the body does not show.
    //
    // useCounty, not selectedCounty, because it is the flag readAt uses to pick
    // the county row: "County" in the heading therefore means county numbers on
    // the bars, and cannot drift from them.
    const heading = options && compare
        ? (useCounty ? 'County vs. State' : 'State vs. U.S.')
        : 'U.S. Totals';
    // The legend reads its text off series.name, so seriesLabel and
    // comparisonLabel above are the only place a geography gets named and
    // there is no second set of strings to keep in step with them.
    // Both gates sit below every hook so the hook order stays stable across
    // renders when a selection change flips either one.
    if (!chartAllowed(categoryId, getNarrativeScope(dashboardState)))
        return null;
    if (!categoryId || !metrics.length)
        return null;
    // .panel-flow is the box that sits below the sidebar panel - painted as a
    // continuation of it on screen, a separate card on paper. It is rendered
    // here rather than by MapDashboard so that the gates above, which return
    // null for most categories, take the box with them instead of leaving an
    // empty bordered strip. Spacing and the divider above the chart come from
    // that box, which is why no margin utilities are left on the block.
    return (_jsx("div", { className: "panel-flow", children: _jsxs("div", { className: "compare-chart-block", children: [_jsx("h3", { className: SECTION_LABEL_CLASS, children: heading }), _jsx("div", { children: isLoading ? null : options ? (_jsx(HighchartsReact, { highcharts: Highcharts, options: options })) : (_jsxs("p", { className: "small text-black-50 mb-0", children: ["Chart data for ", ((_b = category === null || category === void 0 ? void 0 : category.label) !== null && _b !== void 0 ? _b : 'this category').toLowerCase(), " coming soon."] })) })] }) }));
};
export default CompareChart;
